import { useState, useEffect, useRef, useCallback } from "react";
import { evolveBadges } from "@/lib/badges";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api, { BACKEND_URL } from "@/lib/api";
import { getPlaylistCoverUrl, getCoverUrl as strictGetCoverUrl, getMusicUrl, optimizeImage } from "@/lib/mediaUrls";
import { getDisplayPlays, formatDisplayPlays } from "@/lib/promoPlays";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import BackButton from "@/components/BackButton";

// Icons
const PlayIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
);

const MusicIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
  </svg>
);

const TrashIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const EditIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const LockIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const GlobeIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
  </svg>
);

// Badge Component
const Badge = ({ type }) => {
  const badges = {
    founder: { label: "Founder", className: "bg-gradient-to-r from-yellow-500 to-amber-600 text-black" },
    founding_artist: { label: "Founding Artist", className: "bg-gradient-to-r from-yellow-400 to-amber-500 text-black" },
    admin: { label: "Admin", className: "bg-purple-600 text-white" },
    spotlight_winner: { label: "Spotlight", className: "bg-gradient-to-r from-pink-500 to-purple-600 text-white" },
    producer: { label: "Producer", className: "bg-blue-600 text-white" }
  };
  const badge = badges[type];
  if (!badge) return null;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${badge.className}`}>
      {badge.label}
    </span>
  );
};

// Helper to get cover URL — strict Cloudinary-only (track object preferred)
const getCoverUrl = (track) => {
  if (!track) return null;
  if (typeof track === 'string') {
    return track.startsWith('http') ? track : null;
  }
  return strictGetCoverUrl(track);
};

// Helper to get audio URL — strict Cloudinary-only
const getAudioUrl = (track) => {
  if (!track) return null;
  if (typeof track === 'string') {
    return track.startsWith('http') ? track : null;
  }
  return getMusicUrl(track) || null;
};

// Track Row Component
const TrackRow = ({ track, index, isPlaying, onPlay, onRemove, isOwner, playGain }) => {
  const coverUrl = getCoverUrl(track);
  const artistName = track.artist?.artist_name || track.artist?.name || track.artist_name || 'Unknown Artist';
  
  return (
    <div 
      className={`flex items-center gap-4 p-3 rounded-lg transition-colors group ${
        isPlaying ? 'bg-purple-900/30' : 'hover:bg-white/5'
      }`}
      data-testid={`track-row-${track.submission_id}`}
    >
      {/* Track number / Play button */}
      <div className="w-8 flex justify-center">
        <button
          onClick={() => onPlay(track, index)}
          className="text-gray-400 group-hover:text-white transition-colors"
        >
          {isPlaying ? (
            <span className="text-purple-400 font-bold">{index + 1}</span>
          ) : (
            <span className="group-hover:hidden">{index + 1}</span>
          )}
          <PlayIcon className="w-5 h-5 hidden group-hover:block" />
        </button>
      </div>
      
      {/* Cover art */}
      <div className="w-12 h-12 rounded overflow-hidden bg-purple-900/30 flex-shrink-0">
        {coverUrl ? (
          <img src={optimizeImage(coverUrl, 'card')} alt={track.track_title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <MusicIcon className="w-6 h-6 text-purple-400/50" />
          </div>
        )}
      </div>
      
      {/* Track info */}
      <div className="flex-1 min-w-0">
        <Link 
          to={`/track/${track.submission_id}`}
          className={`block font-medium truncate hover:text-purple-300 transition-colors ${
            isPlaying ? 'text-purple-300' : 'text-white'
          }`}
        >
          {track.track_title || 'Untitled Track'}
        </Link>
        <div className="flex items-center gap-2 mt-1">
          <Link 
            to={`/artist/${track.user_id}`}
            className="text-sm text-gray-400 hover:text-white truncate"
          >
            {artistName}
          </Link>
          {evolveBadges(track.artist?.badges)?.map((badge) => (
            <Badge key={badge} type={badge} />
          ))}
        </div>
      </div>
      
      {/* Stats */}
      <div className="hidden sm:flex items-center gap-4 text-sm text-gray-400">
        <span title="Total plays">▶ {formatDisplayPlays(getDisplayPlays(track))}</span>
        <span>❤️ {track.likes?.length || 0}</span>
        {playGain > 0 && (
          <span
            className="px-2 py-0.5 rounded-full bg-green-500/20 text-green-300 text-xs"
            title="Plays gained from this playlist"
            data-testid={`play-gain-${track.submission_id}`}
          >
            +{playGain} from playlist
          </span>
        )}
      </div>
      
      {/* Remove button (owner only) */}
      {isOwner && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(track.submission_id);
          }}
          className="p-2 text-gray-500 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Remove from playlist"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const PlaylistDetailPage = () => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const audioRef = useRef(null);
  
  const [currentUser, setCurrentUser] = useState(null);
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Playback state
  const [currentTrackIndex, setCurrentTrackIndex] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  
  // Edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverProgress, setCoverProgress] = useState(0);
  const [coverPreview, setCoverPreview] = useState(null);
  const [editDescription, setEditDescription] = useState("");
  const [editIsPublic, setEditIsPublic] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  // Phase 3.2: track which submissions have already had a play counted in this session
  const playCountedRef = useRef(new Set());
  
  useEffect(() => {
    fetchCurrentUser();
    fetchPlaylist();
    // fetchCurrentUser + fetchPlaylist are stable enough for this initial-
    // load effect; only re-run when the URL playlistId changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlistId]);
  
  useEffect(() => {
    if (playlist) {
      setEditName(playlist.name);
      setEditDescription(playlist.description || "");
      setEditIsPublic(playlist.is_public);
    }
  }, [playlist]);
  
  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setCurrentUser(response.data);
    } catch (error) {
      // User not logged in
    }
  };
  
  const fetchPlaylist = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/playlists/${playlistId}`);
      setPlaylist(response.data);
      setError(null);
    } catch (error) {
      console.error('Failed to fetch playlist:', error);
      setError(error.response?.data?.detail || "Playlist not found");
    } finally {
      setLoading(false);
    }
  };
  
  const handlePlay = (track, index) => {
    if (currentTrackIndex === index && isPlaying) {
      // Pause current track
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      // Play new track or resume
      setCurrentTrackIndex(index);
      
      if (audioRef.current) {
        const audioUrl = getAudioUrl(track);
        if (!audioUrl) {
          toast.error('Audio unavailable for this track');
          setIsPlaying(false);
          return;
        }
        if (audioRef.current.src !== audioUrl) {
          audioRef.current.src = audioUrl;
        }
        audioRef.current.play().catch(console.error);
        setIsPlaying(true);
      }
    }
  };
  
  const handlePlayAll = () => {
    if (playlist?.tracks?.length > 0) {
      handlePlay(playlist.tracks[0], 0);
    }
  };
  
  const handleNextTrack = () => {
    if (playlist?.tracks && currentTrackIndex !== null) {
      const nextIndex = currentTrackIndex + 1;
      if (nextIndex < playlist.tracks.length) {
        handlePlay(playlist.tracks[nextIndex], nextIndex);
      } else {
        // End of playlist
        setIsPlaying(false);
        setCurrentTrackIndex(null);
      }
    }
  };
  
  const handleRemoveTrack = async (trackId) => {
    if (!confirm("Remove this track from the playlist?")) return;
    
    try {
      await api.delete(`/playlists/${playlistId}/tracks/${trackId}`);
      toast.success("Track removed");
      fetchPlaylist();
    } catch (error) {
      toast.error("Failed to remove track");
    }
  };
  
  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      await api.patch(`/playlists/${playlistId}`, {
        name: editName,
        description: editDescription || null,
        is_public: editIsPublic
      });
      toast.success("Playlist updated");
      setIsEditing(false);
      fetchPlaylist();
    } catch (error) {
      toast.error("Failed to update playlist");
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeletePlaylist = async () => {
    if (!confirm("Delete this playlist? This cannot be undone.")) return;
    
    try {
      await api.delete(`/playlists/${playlistId}`);
      toast.success("Playlist deleted");
      navigate("/playlists");
    } catch (error) {
      toast.error("Failed to delete playlist");
    }
  };
  
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type", { description: "Use JPG, PNG, or WebP." });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large", { description: "Maximum 5MB." });
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (ev) => setCoverPreview(ev.target.result);
    reader.readAsDataURL(file);

    const formData = new FormData();
    formData.append('cover', file);

    setUploadingCover(true);
    setCoverProgress(0);
    try {
      await api.post(`/playlists/${playlistId}/cover`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            setCoverProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      });
      toast.success("Cover image updated");
      // Refresh playlist so the saved filename URL is rendered
      await fetchPlaylist();
      setCoverPreview(null);
    } catch (error) {
      toast.error("Failed to upload cover", {
        description: error.response?.data?.detail || "Please try again",
      });
      setCoverPreview(null);
    } finally {
      setUploadingCover(false);
      setCoverProgress(0);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={currentUser} activePage="playlists" />
        <div className="pt-24 flex items-center justify-center h-[calc(100vh-6rem)]">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={currentUser} activePage="playlists" />
        <div className="pt-24 flex flex-col items-center justify-center h-[calc(100vh-6rem)]">
          <p className="text-red-400 text-xl mb-4">{error}</p>
          <Link to="/playlists">
            <Button className="bg-purple-600 hover:bg-purple-500">Back to Playlists</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Phase 3.2: defensive — if playlist is somehow null after loading=false without error, show fallback
  if (!playlist) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={currentUser} activePage="playlists" />
        <div className="pt-24 flex flex-col items-center justify-center h-[calc(100vh-6rem)]">
          <p className="text-gray-400 text-xl mb-4">Playlist unavailable</p>
          <Link to="/playlists">
            <Button className="bg-purple-600 hover:bg-purple-500">Back to Playlists</Button>
          </Link>
        </div>
      </div>
    );
  }
  
  const coverUrl = playlist.cover_image ? getPlaylistCoverUrl(playlist) : null;
  
  const isOwner = currentUser && playlist.is_owner;
  const creatorName = playlist.creator?.artist_name || playlist.creator?.name || 'Unknown';
  
  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="playlist-detail-page">
      <SharedNavigation currentUser={currentUser} activePage="playlists" />
      
      {/* Hidden audio element */}
      <audio
        ref={audioRef}
        onEnded={handleNextTrack}
        onTimeUpdate={() => {
          if (audioRef.current) {
            setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
            // Phase 3.2: count play after 10s of listening, attribute to this playlist
            const cur = currentTrackIndex !== null ? playlist?.tracks?.[currentTrackIndex] : null;
            if (cur && audioRef.current.currentTime >= 10 && !playCountedRef.current.has(cur.submission_id)) {
              playCountedRef.current.add(cur.submission_id);
              api.post(`/track/${cur.submission_id}/play-count?playlist_id=${playlistId}`).catch(() => {});
            }
          }
        }}
      />
      
      <div className="pt-20">
        {/* Hero Header */}
        <div className="bg-gradient-to-b from-purple-900/30 to-transparent pb-8">
          <div className="max-w-7xl mx-auto px-4 pt-8">
            <div className="mb-4">
              <BackButton fallback="/playlists" testId="playlist-back-button" />
            </div>
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              {/* Playlist Cover */}
              <div className="relative group">
                <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-lg overflow-hidden bg-gradient-to-br from-purple-900/50 to-pink-900/50 shadow-2xl" data-testid="playlist-cover-container">
                  {(coverPreview || coverUrl) ? (
                    <img src={coverPreview || coverUrl} alt={playlist.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <MusicIcon className="w-24 h-24 text-purple-400/50" />
                    </div>
                  )}
                  {/* Upload progress overlay (always visible while uploading) */}
                  {uploadingCover && (
                    <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center" data-testid="playlist-cover-uploading">
                      <svg className="animate-spin h-8 w-8 text-white mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <p className="text-white text-xs font-bold">{coverProgress}%</p>
                      <div className="w-32 h-1 bg-gray-700 rounded-full overflow-hidden mt-1">
                        <div className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all" style={{ width: `${coverProgress}%` }}></div>
                      </div>
                    </div>
                  )}
                </div>

                {isOwner && !uploadingCover && (
                  <label className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer rounded-lg">
                    <span className="text-white text-sm font-medium">Change Cover</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleCoverUpload}
                      className="hidden"
                      data-testid="playlist-cover-input"
                    />
                  </label>
                )}
              </div>
              
              {/* Playlist Info */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {playlist.is_public ? (
                    <span className="flex items-center gap-1 text-sm text-gray-400">
                      <GlobeIcon className="w-4 h-4" />
                      Public Playlist
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-gray-400">
                      <LockIcon className="w-4 h-4" />
                      Private Playlist
                    </span>
                  )}
                </div>
                
                {isEditing ? (
                  <div className="space-y-4">
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="text-2xl font-bold bg-black/50 border-purple-900/50"
                      placeholder="Playlist name"
                    />
                    <Textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      className="bg-black/50 border-purple-900/50 resize-none"
                      placeholder="Description (optional)"
                      rows={2}
                    />
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={editIsPublic}
                        onChange={(e) => setEditIsPublic(e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-gray-300">Public playlist</span>
                    </label>
                    <div className="flex gap-2">
                      <Button
                        onClick={handleSaveEdit}
                        disabled={isSaving}
                        className="bg-purple-600 hover:bg-purple-500"
                      >
                        {isSaving ? "Saving..." : "Save"}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setIsEditing(false)}
                        className="border-gray-600"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h1 className="text-4xl sm:text-5xl font-black text-white mb-3">{playlist.name}</h1>
                    
                    {playlist.description && (
                      <p className="text-gray-400 mb-4">{playlist.description}</p>
                    )}
                    
                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
                      <Link to={`/artist/${playlist.creator?.user_id}`} className="hover:text-white">
                        {creatorName}
                      </Link>
                      <span>•</span>
                      <span>{playlist.tracks?.length || 0} tracks</span>
                      {playlist.total_plays_gained > 0 && (
                        <>
                          <span>•</span>
                          <span data-testid="playlist-total-plays-gained" className="text-green-300">
                            +{playlist.total_plays_gained} plays gained
                          </span>
                        </>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-3">
                      {playlist.tracks?.length > 0 && (
                        <Button
                          onClick={handlePlayAll}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-bold"
                          data-testid="play-all-btn"
                        >
                          <PlayIcon className="w-5 h-5 mr-2" />
                          Play All
                        </Button>
                      )}
                      
                      {isOwner && (
                        <>
                          <Button
                            variant="outline"
                            onClick={() => setIsEditing(true)}
                            className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                          >
                            <EditIcon className="w-4 h-4 mr-2" />
                            Edit
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleDeletePlaylist}
                            className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                          >
                            <TrashIcon className="w-4 h-4 mr-2" />
                            Delete
                          </Button>
                        </>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Tracks List */}
        <div className="max-w-7xl mx-auto px-4 py-8">
          {playlist.tracks?.length === 0 ? (
            <div className="text-center py-16">
              <MusicIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">This playlist is empty</h3>
              <p className="text-gray-400 mb-6">
                {isOwner 
                  ? "Add tracks from the discover feed or track pages"
                  : "The owner hasn't added any tracks yet"
                }
              </p>
              {isOwner && (
                <Link to="/discover">
                  <Button className="bg-purple-600 hover:bg-purple-500">
                    Discover Tracks
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-1">
              {/* Now Playing Progress */}
              {currentTrackIndex !== null && (
                <div className="mb-4 bg-purple-900/20 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-purple-300">
                      Now Playing: {playlist.tracks[currentTrackIndex]?.track_title}
                    </span>
                    <button
                      onClick={() => handlePlay(playlist.tracks[currentTrackIndex], currentTrackIndex)}
                      className="text-white"
                    >
                      {isPlaying ? <PauseIcon className="w-6 h-6" /> : <PlayIcon className="w-6 h-6" />}
                    </button>
                  </div>
                  <div className="h-1 bg-purple-900/50 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-200"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
              
              {playlist.tracks.map((track, index) => (
                <TrackRow
                  key={track.submission_id}
                  track={track}
                  index={index}
                  isPlaying={currentTrackIndex === index && isPlaying}
                  onPlay={handlePlay}
                  onRemove={handleRemoveTrack}
                  isOwner={isOwner}
                  playGain={playlist.track_play_gain?.[track.submission_id] || 0}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlaylistDetailPage;
